import axios from "../axios";
import toast from "react-hot-toast";

export interface SSODomainItem {
  id: number;
  sso_connection_id: number;
  domain: string;
  dns_txt_record: string;
  is_verified: boolean;
  verified_at?: string | null;
}

export interface SSOConnectionData {
  id: number;
  company_id: number;
  protocol: "oidc" | "saml";
  provider: string;
  display_name: string;
  oidc_issuer?: string | null;
  oidc_client_id?: string | null;
  oidc_client_secret?: string | null;
  status: "draft" | "testing" | "active" | "disabled";
  auto_create_users: boolean;
  default_role: number;
  Domains?: SSODomainItem[];
}

export interface GetSSOResponse {
  connection: SSOConnectionData | null;
  enforce_sso: boolean;
}

/**
 * Get company's current SSO connection and settings
 */
export const getMyCompanySSO = async (): Promise<GetSSOResponse | null> => {
  try {
    const response = await axios.get("/v2/sso/my-settings");
    return response.data;
  } catch (error: any) {
    console.error("Error fetching SSO settings:", error);
    toast.error(error?.response?.data?.message || "Failed to load SSO settings");
    return null;
  }
};

/**
 * Save / update SSO connection details
 */
export const saveSSOConnection = async (payload: {
  provider?: string;
  display_name?: string;
  oidc_issuer?: string;
  oidc_client_id?: string;
  oidc_client_secret?: string;
  status?: string;
}) => {
  try {
    const response = await axios.post("/v2/sso/connection", payload);
    if (response.data.is_reachable === false) {
      toast.error(response.data.message || "SSO details saved as Draft, but CTB could not reach your Okta Issuer URL.");
    } else {
      toast.success(response.data.message || "SSO settings saved & verified!");
    }
    return response.data;
  } catch (error: any) {
    console.error("Error saving SSO connection:", error);
    toast.error(error?.response?.data?.message || "Failed to save SSO connection");
    return null;
  }
};

/**
 * Add a new domain to company SSO connection
 */
export const addSSODomain = async (domain: string) => {
  try {
    const response = await axios.post("/v2/sso/domains", { domain });
    toast.success(response.data.message || "Domain registered!");
    return response.data.domain;
  } catch (error: any) {
    console.error("Error adding domain:", error);
    toast.error(error?.response?.data?.message || "Failed to add domain");
    return null;
  }
};

/**
 * Verify DNS TXT record for a domain
 */
export const verifySSODomain = async (domain_id: number) => {
  try {
    const response = await axios.post("/v2/sso/domains/verify", { domain_id });
    toast.success(response.data.message || "Domain verified!");
    return response.data.domain;
  } catch (error: any) {
    console.error("Error verifying domain:", error);
    toast.error(error?.response?.data?.message || "Domain DNS verification failed");
    return null;
  }
};

/**
 * Delete a registered domain
 */
export const deleteSSODomain = async (domain_id: number) => {
  try {
    const response = await axios.delete("/v2/sso/domains", { data: { domain_id } });
    toast.success(response.data.message || "Domain removed");
    return true;
  } catch (error: any) {
    console.error("Error removing domain:", error);
    toast.error(error?.response?.data?.message || "Failed to remove domain");
    return false;
  }
};

/**
 * Toggle enforce_sso setting
 */
export const toggleEnforceSSO = async (enforce_sso: boolean) => {
  try {
    const response = await axios.post("/v2/sso/enforce", { enforce_sso });
    toast.success(response.data.message || "SSO enforcement updated");
    return response.data.enforce_sso;
  } catch (error: any) {
    console.error("Error updating SSO enforcement:", error);
    toast.error(error?.response?.data?.message || "Failed to update SSO enforcement");
    return null;
  }
};

/**
 * Exchange one-time short-lived SSO code for user tokens
 */
export const exchangeSSOCode = async (code: string) => {
  try {
    const response = await axios.post("/v2/sso/exchange", { code });
    return response.data;
  } catch (error: any) {
    console.error("Error exchanging SSO code:", error);
    toast.error(error?.response?.data?.message || "Failed to complete SSO authentication");
    return null;
  }
};

/**
 * Validate email and initiate SSO login redirect URL
 */
export const initiateSSOLogin = async (email: string, isTest: boolean = false) => {
  try {
    const response = await axios.get("/v2/sso/login", {
      params: { email, format: "json", is_test: isTest ? "true" : "false" }
    });
    return response.data;
  } catch (error: any) {
    console.error("Error initiating SSO login:", error);
    toast.error(
      error?.response?.data?.message || "Single Sign-On initialization failed"
    );
    return null;
  }
};
